import {
  findNewestToolOutput,
  findNewestPaymentRead,
  maskEmail,
  OrganizationAssistantHandoffCard,
  OrganizationAssistantResendDonorEmailCard,
  OrganizationAssistantSetSalesOpenCard,
} from "@simplyk/common";
import type { ToolUIPart, UIMessage } from "ai";

import { isRecord } from "./isRecord";

import { useCurrentUserContext } from "@/common/contexts/CurrentUserContext";

export type HandoffCardContent = Omit<OrganizationAssistantHandoffCard, "replyEmail"> & { replyEmail: string | null };

export type OrganizationApprovalDescription =
  | { kind: "handoff"; card: HandoffCardContent }
  | { kind: "resend"; card: OrganizationAssistantResendDonorEmailCard | null }
  | { kind: "sales"; card: OrganizationAssistantSetSalesOpenCard }
  | { kind: "unknown" };

const REQUEST_PREVIEW_LENGTH = 200;

export const useOrganizationApprovalDescription = (
  part: ToolUIPart,
  messages: UIMessage[]
): OrganizationApprovalDescription => {
  const { currentUser } = useCurrentUserContext();
  const input = isRecord(part.input) ? part.input : {};

  if (part.type === "tool-requestHumanSupport") {
    return {
      kind: "handoff",
      card: {
        replyEmail: currentUser?.email ? maskEmail(currentUser.email) : null,
        requestPreview: toPreview(typeof input.request === "string" ? input.request : ""),
      },
    };
  }
  if (part.type === "tool-resendDonorEmail") {
    return { kind: "resend", card: findResendCard(messages, normalizeId(input.transactionId)) };
  }
  if (part.type === "tool-setSalesOpen") {
    return {
      kind: "sales",
      card: {
        formName: findFormName(messages, normalizeId(input.ticketingId)),
        effect: input.open === true ? "reopen" : "close",
      },
    };
  }
  return { kind: "unknown" };
};

const normalizeId = (value: unknown): string | null => (typeof value === "string" ? value.trim().toLowerCase() : null);

const toPreview = (request: string): string => {
  const characters = Array.from(request);
  return characters.length > REQUEST_PREVIEW_LENGTH
    ? `${characters.slice(0, REQUEST_PREVIEW_LENGTH).join("")}…`
    : request;
};

const findResendCard = (
  messages: UIMessage[],
  transactionId: string | null
): OrganizationAssistantResendDonorEmailCard | null => {
  const payment = transactionId ? findNewestPaymentRead(messages, transactionId) : null;
  if (
    !payment ||
    typeof payment.donorEmail !== "string" ||
    typeof payment.amount !== "number" ||
    typeof payment.currency !== "string" ||
    typeof payment.createdAt !== "string"
  ) {
    return null;
  }
  return {
    donorEmail: maskEmail(payment.donorEmail),
    amount: payment.amount,
    currency: payment.currency,
    createdAt: payment.createdAt,
    formName: typeof payment.formName === "string" ? payment.formName : null,
  };
};

const findFormName = (messages: UIMessage[], ticketingId: string | null): string | null => {
  if (!ticketingId) {
    return null;
  }
  const form = findNewestToolOutput(messages, "getForm", (output) => output.id === ticketingId);
  if (form) {
    return typeof form.name === "string" ? form.name : null;
  }
  const listedForm = findListedForm(
    findNewestToolOutput(messages, "listForms", (output) => findListedForm(output, ticketingId) !== null),
    ticketingId
  );
  return listedForm && typeof listedForm.name === "string" ? listedForm.name : null;
};

const findListedForm = (output: Record<string, unknown> | null, formId: string): Record<string, unknown> | null => {
  const forms: unknown = output?.forms;
  return (
    (Array.isArray(forms) ? forms : []).find(
      (item: unknown): item is Record<string, unknown> => isRecord(item) && item.id === formId
    ) ?? null
  );
};
